import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Reveal from "@/components/Reveal";
import MutedVideo from "@/components/MutedVideo";
import PriceTable from "@/components/PriceTable";
import PriceList from "@/components/PriceList";
import ShareButton from "@/components/ShareButton";
import { ArrowLeft, ArrowRight, Check, Sparkles, Info } from "lucide-react";
import { TREAT_DETAILS } from "@/data/site";

export default function TreatDetail() {
    const { slug } = useParams();
    const treat = TREAT_DETAILS[slug];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!treat) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-[#FFFDF9] px-6 text-center" data-testid="treat-not-found">
                <p className="font-script text-4xl text-[#C95B7D]">Oops, that treat isn't on the menu</p>
                <Link to="/" data-testid="treat-not-found-home" className="mt-6 rounded-full bg-[#C95B7D] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#b44a6b] transition-colors">
                    Back to Home
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FFFDF9]" data-testid={`treat-detail-${slug}`}>
            <header className="fixed inset-x-0 top-0 z-50 border-b border-[#F2E1E6] bg-[#FFFDF9]/90 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-20">
                    <Link to="/" data-testid="treat-detail-logo" className="flex items-center gap-3">
                        <img src="/annabells-logo.png" alt="Annabells Cupcakes and More logo" className="h-12 w-12 rounded-full object-cover ring-2 ring-[#D4AF37]/50 shadow-md" />
                        <span className="leading-none">
                            <span className="font-script text-2xl text-[#C95B7D] block">Annabells</span>
                            <span className="text-[10px] tracking-[0.3em] uppercase text-[#8C6A5D] font-semibold">Cupcakes &amp; More</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <ShareButton title={`${treat.name} · Annabells Cupcakes and More`} path={`/treats/${slug}`} testId="treat-detail-share" />
                        <Link
                            to="/#treats"
                            data-testid="treat-detail-back"
                            className="inline-flex items-center gap-2 rounded-full border border-[#3D2318]/15 bg-white/70 px-5 py-2.5 text-sm font-semibold text-[#3D2318] hover:border-[#C59B27]/60 hover:bg-[#FAF0D7]/70 transition-[background-color,border-color] duration-300"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            <span className="hidden sm:inline">All Treats</span>
                            <span className="sm:hidden">Back</span>
                        </Link>
                    </div>
                </div>
            </header>

            <section className="pt-32 pb-16 md:pt-40 md:pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-6">
                            <Reveal>
                                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Annabells Cupcakes and More</p>
                                <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#3D2318]" data-testid="treat-detail-title">
                                    {treat.name}
                                </h1>
                                <p className="font-script text-3xl sm:text-4xl text-[#C95B7D] mt-1">{treat.script}</p>
                                <p className="mt-5 max-w-xl text-base sm:text-lg text-[#6B4A3E] font-light leading-relaxed">{treat.intro}</p>
                            </Reveal>

                            {treat.includes && (
                                <Reveal delay={0.1}>
                                    <ul className="mt-8 space-y-3">
                                        {treat.includes.map((item) => (
                                            <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-[#6B4A3E]">
                                                <Check className="h-5 w-5 shrink-0 text-[#C95B7D] mt-0.5" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </Reveal>
                            )}

                            {treat.priceTable && (
                                <Reveal delay={0.12}>
                                    <div className="mt-8 rounded-2xl bg-white border border-[#F2E1E6] p-5 sm:p-6 shadow-[0_16px_40px_rgba(61,35,24,0.08)]">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]">{treat.priceTableTitle}</p>
                                        <div className="mt-3"><PriceTable table={treat.priceTable} testId="treat-detail-price-table" /></div>
                                    </div>
                                </Reveal>
                            )}

                            {treat.priceList && (
                                <Reveal delay={0.12}>
                                    <div className="mt-8 rounded-2xl bg-white border border-[#F2E1E6] px-5 sm:px-6 py-3 shadow-[0_16px_40px_rgba(61,35,24,0.08)]">
                                        <p className="pt-3 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]">{treat.priceListTitle}</p>
                                        <PriceList items={treat.priceList} testId="treat-detail-price-list" />
                                    </div>
                                </Reveal>
                            )}

                            {treat.notes && (
                                <Reveal delay={0.15}>
                                    <div className="mt-8 space-y-2.5 rounded-2xl border border-[#D4AF37]/40 bg-[#FAF0D7]/70 p-5" data-testid="treat-detail-notes">
                                        {treat.notes.map((note) => (
                                            <p key={note} className="flex items-start gap-2.5 text-sm text-[#6B4A3E] leading-relaxed">
                                                <Info className="h-4 w-4 shrink-0 text-[#C59B27] mt-0.5" />
                                                {note}
                                            </p>
                                        ))}
                                    </div>
                                </Reveal>
                            )}

                            {treat.flavors && (
                                <Reveal delay={0.2}>
                                    <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]">{treat.flavorsTitle}</p>
                                    <div className="mt-3 flex flex-wrap gap-2" data-testid="treat-detail-flavors">
                                        {treat.flavors.map((f) => (
                                            <span key={f} className="rounded-full bg-[#FDF0F4] border border-[#F2E1E6] px-3.5 py-1.5 text-xs font-medium text-[#C95B7D]">
                                                {f}
                                            </span>
                                        ))}
                                    </div>
                                    {treat.extraFlavors && (
                                        <>
                                            <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.25em] text-[#C59B27]">{treat.extraFlavorsTitle}</p>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {treat.extraFlavors.map((f) => (
                                                    <span key={f} className="rounded-full bg-white border border-[#F2E1E6] px-3.5 py-1.5 text-xs font-medium text-[#6B4A3E]">
                                                        {f}
                                                    </span>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </Reveal>
                            )}

                            <Reveal delay={0.25}>
                                <Link
                                    to="/#order"
                                    data-testid="treat-detail-order-button"
                                    className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#C95B7D] px-8 py-4 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(201,91,125,0.4)] hover:bg-[#b44a6b] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
                                >
                                    Request an Order
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                            </Reveal>
                        </div>

                        <div className="lg:col-span-6">
                            <Reveal delay={0.1}>
                                <div className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] border-[10px] border-white shadow-[0_30px_80px_rgba(61,35,24,0.22)]">
                                    <img src={treat.hero} alt={treat.name} data-testid="treat-detail-hero-image" className="h-[380px] sm:h-[480px] w-full object-cover" />
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <section className="pb-16 md:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">
                            <Sparkles className="h-4 w-4" />
                            From the Kitchen
                        </p>
                    </Reveal>
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="treat-detail-gallery">
                        {treat.images.map((img, i) => (
                            <Reveal key={i} delay={i * 0.08}>
                                <figure className="group overflow-hidden rounded-2xl" data-testid={`treat-detail-image-${i}`}>
                                    {img.video ? (
                                        <MutedVideo video={img.video} testId={`treat-detail-video-${i}`} className="h-48 sm:h-64 w-full object-cover" />
                                    ) : (
                                        <img
                                            src={img.src}
                                            alt={img.alt}
                                            className="h-48 sm:h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                    )}
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <footer className="bg-[#2C180F] py-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-[#FFFDF9]/40">© {new Date().getFullYear()} Annabells Cupcakes and More · Homemade with love in Washington</p>
                    <Link to="/" data-testid="treat-detail-footer-home" className="text-xs font-semibold text-[#F8D7E3] hover:text-[#D4AF37] transition-colors">
                        Back to Home
                    </Link>
                </div>
            </footer>
        </div>
    );
}
